import React, { useContext, useEffect, useMemo, useState } from "react";
import Modal from "components/ui/Modal";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { getIcon } from "utils/iconsConfig";
import "./ordersModal.css";
import CalenderComp from "./CalenderComp";
import { capitalEachLetter } from "utils/capitalEachLetter";
import { formatDate } from "utils/formatDate";
import BinnysChakra from "../../../../assets/icons/Binnys Chakra.svg";
import config from "configs/config";
import { useGlobalContext } from "context/GlobalContext";
import { SocketContext } from "context/SocketContext";
import { useQuery } from "@tanstack/react-query";
import apiClient from "lib/api-client";
import { usePermissions } from "context/PermissionsContext";

const OrdersNotificationModal = ({ open, handleClose }) => {
  const [showCalendar, setShowCalendar] = React.useState(false);

  const [selectedDate, setSelectedDate] = React.useState(null);
  const [orders, setOrders] = useState([]);
  const [meltingLogsState, setMeltingLogsState] = useState([]);
  const [orderCount, setOrderCount] = useState(0);
  const [meltingCount, setMeltingCount] = useState(0);
  const previousLength = React.useRef(0);
  const isFirstRun = React.useRef(true);
  const { setNotificationCount } = useGlobalContext();
  const socket = useContext(SocketContext);
  // const permissions = usePermissions();
  const userRole = JSON.parse(localStorage.getItem("userData"));

  const getMeltingLogs = async () => {
    const res = await apiClient.get("/api/v1/melting_request/meltingLogs/get");
    return res.data.data;
  };

  const { data: logs, refetch, isLoading } = useQuery({
    queryKey: ["meltingLogs"],
    queryFn: getMeltingLogs,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!userRole?.role && userRole?.role?.isAdmin,
  });

  useEffect(() => {

    if (userRole?.role || !userRole?.role?.isAdmin) return;

    const handler = () => {
      refetch();
    };

    socket.on("melting-logs-updated", handler);

    return () => {
      socket.off("melting-logs-updated", handler);
    };
  }, [refetch, socket]);

  const groupLogsByDate = (logs = []) => {
    const grouped = {};

    logs.forEach((log) => {
      const date = new Date(log.timing).toISOString().split("T")[0];

      if (!grouped[date]) {
        grouped[date] = [];
      }

      grouped[date].push({
        message: log.message,
        isRead: log.isRead,
        type: "melting",
      });
    });

    return Object.entries(grouped).map(([date, notifications]) => ({
      date,
      notifications,
    }));
  };

  const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  }

  const setCookie = (name, value, days = 7) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  };

  useEffect(() => {
    const eventSource = new EventSource(`${config.baseUrl}/api/v1/catalogSetting/catalog-client/getOrderDetailForNotification`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.success && Array.isArray(data.data)) {
          const allNotifications = data.data.flatMap((group) => group.notifications);
          const newLength = allNotifications.length;

          const currentTotal = parseInt(getCookie("totalNotificationCount") || "0", 10);
          const meltingTotal = parseInt(getCookie("totalMeltingNotificationCount") || "0", 10);

          if (isFirstRun.current) {
            if (currentTotal && newLength > currentTotal) {
              const missedCount = newLength - currentTotal;
              setNotificationCount(missedCount);
              setCookie("notificationCount", missedCount);
              setCookie("totalNotificationCount", newLength);
            } else {
              setCookie("totalNotificationCount", newLength);
            }

            previousLength.current = newLength;
            isFirstRun.current = false;
            setOrders(data.data);
            return;
          }

          const diff = newLength - previousLength.current;

          if (diff > 0) {
            const newNotifications = allNotifications.slice(0, diff);
            previousLength.current = newLength;
            setOrders(data.data);

            if (Notification.permission === "granted") {
              newNotifications.forEach((notif) => {
                try {
                  new Notification("New Order Received", {
                    body: `A new order has been placed by ${capitalizeFirstLetter(notif.name)} of Catalog #${notif.catalogId}`,
                    icon: BinnysChakra,
                  }).onclick = () => window.focus();
                } catch (err) {
                  console.error("Notification failed:", err);
                }
              });
            } else {
              Notification.requestPermission();
            }

            // setNotificationCount((prev) => {
            //   const next = prev + diff;
            //   setCookie("notificationCount", next);
            //   return next;
            // });
            setOrderCount((prev) => prev + diff);
            setCookie("totalNotificationCount", newLength);
          } else {
            setOrders(data.data);
          }

          // if (newLength > currentTotal) {
          //   setCookie("totalNotificationCount", newLength);
          // }
        }
      } catch (err) {
        console.error("Error parsing SSE data:", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("SSE connection error:", err);
      eventSource.close();
    };

    return () => eventSource.close();
  }, []);

  useEffect(() => {
    if (!logs) return;

    const meltingFormatted = groupLogsByDate(logs);
    setMeltingLogsState(meltingFormatted);

    const unreadCount = logs.filter((log) => !log.isRead).length;

    setMeltingCount(unreadCount);
  }, [logs]);

  useEffect(() => {
    const total = orderCount + meltingCount;

    setNotificationCount(total);

    setCookie("notificationCount", orderCount);
    setCookie("totalMeltingNotificationCount", meltingCount);

  }, [orderCount, meltingCount]);

  const combinedData = useMemo(() => {
    const combined = orders.map((day) => ({
      ...day,
      notifications: [...day.notifications],
    }));

    meltingLogsState.forEach((meltingDay) => {
      const existingDay = combined.find(
        (d) => d.date === meltingDay.date
      );

      if (existingDay) {
        const existingMessages = new Set(
          existingDay.notifications.map((n) => n.message)
        );

        const newNotifications = meltingDay.notifications.filter(
          (n) => !existingMessages.has(n.message)
        );

        existingDay.notifications = [
          ...newNotifications,
          ...existingDay.notifications,
        ];
      } else {
        combined.push(meltingDay);
      }
    });

    return combined.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [orders, meltingLogsState]);

  const handleDateSelect = (formattedDate) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const format = (date) => {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${String(date.getDate()).padStart(2, "0")} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    if (formattedDate === format(today)) {
      setSelectedDate("Today");
    } else if (formattedDate === format(yesterday)) {
      setSelectedDate("Yesterday");
    } else {
      setSelectedDate(formattedDate);
    }

    setShowCalendar(false);
  };

  const filteredOrders = selectedDate
    ? combinedData.filter((order) => {
      if (selectedDate === "Today") return order.date === "Today";
      if (selectedDate === "Yesterday") return order.date === "Yesterday";
      return formatDate(order.date) === selectedDate;
    })
    : combinedData;

  const toggleCalendar = () => setShowCalendar((prev) => !prev);

  return (
    <>
      <Modal open={open} handleClose={handleClose} style={{ width: "430px", height: "570px" }}>
        <Box className="order-modal">
          <Box className="top-section">
            <Typography variant="h5">Quote Notification</Typography>
            <IconButton onClick={toggleCalendar}>{getIcon("date", "black")}</IconButton>
          </Box>

          <Divider orientation="horizontal" sx={{ margin: 0, padding: 0 }} />

          <Box className="orders-list">
            {filteredOrders.length > 0 ? (
              filteredOrders.map((day, ind) => (
                <div key={ind}>
                  <div className="day-section">
                    <span></span>
                    <strong>{day.date === "Today" || day.date === "Yesterday" ? day.date : formatDate(day.date)}</strong>
                  </div>

                  {day.notifications.map((noti, idx) => (
                    <div key={idx}>
                      <div className="notification">
                        <p>
                          {noti.type === "melting"
                            ? noti.message
                            : `${capitalEachLetter(noti.name)} (${noti.phone}) Have Placed Order Of Catalog #${noti.catalogId}`}
                        </p>
                      </div>
                      {idx !== day.notifications.length - 1 && <Divider orientation="horizontal" />}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                No orders found
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>

      <CalenderComp open={showCalendar} handleClose={toggleCalendar} onDateSelect={handleDateSelect} disableRestrictedDates={false} />
    </>
  );
};

export default OrdersNotificationModal;
